// An option to select color variants based on the selected pack count Code

if (!customElements.get('pack-update-selector')) {
  customElements.define(
    'pack-update-selector',
    class PackUpdateSelector extends HTMLElement {
      connectedCallback() {
        if (this.isConnectedToPackSelector) return;
        this.isConnectedToPackSelector = true;

        this.variantSelects = this.closest('variant-selects');
        this.productInfo = this.closest('product-info');
        this.packInput = this.querySelector('[data-pack-property]');
        this.packLabel = this.querySelector('[data-pack-label-value]');
        this.colourSummary = this.querySelector('[data-colour-summary]');
        this.productFormElement = this.productInfo?.querySelector('product-form');
        this.form = this.productFormElement?.querySelector('form');

        this.restoreState();
        this.syncFromNativeColour();
        this.bindEvents();
      }

      bindEvents() {
        this.sameColorCheckbox = this.querySelector('[data-same-color-checkbox]');
        this.sameColorCount = this.querySelector('[data-same-color-count]');

        if (this.sameColorCheckbox) {
          this.sameColorCheckbox.addEventListener('change', () => {
            if (this.sameColorCheckbox.checked) {
              const primaryColour = this.querySelector('[data-colour-property][data-colour-index="1"]')?.value || this.selectedNativeColour;
              if (primaryColour) this.applySameColourToAll(primaryColour);
            }
          });
        }

        this.addEventListener('click', (event) => {
          const packButton = event.target.closest('[data-pack-option]');
          if (packButton) {
            this.setPack(packButton.dataset.packOption);
            return;
          }

          const colourButton = event.target.closest('[data-colour-target]');
          if (!colourButton || colourButton.getAttribute('aria-disabled') === 'true') return;

          const colourIndex = parseInt(colourButton.dataset.colourTarget, 10);
          const colourValue = colourButton.dataset.colourValue;

          // If checkbox is checked, uncheck it and only change the clicked colour
          if (this.sameColorCheckbox && this.sameColorCheckbox.checked) {
            this.sameColorCheckbox.checked = false;
            this.saveState({ sameColour: false });
          }

          if (colourIndex === 1) {
            this.selectPrimaryColour(colourValue);
          } else {
            this.selectPackColour(colourIndex, colourValue);
          }
        });

        this.variantSelects?.addEventListener('change', () => this.syncFromNativeColour());

        if (this.form) {
          if (this.form._packUpdateSubmitHandler) {
            this.form.removeEventListener('submit', this.form._packUpdateSubmitHandler, true);
          }
          this.form._packUpdateSubmitHandler = this.onPackSubmit.bind(this);
          this.form.addEventListener('submit', this.form._packUpdateSubmitHandler, true);
        }

        // Restore checkbox state now that sameColorCheckbox is assigned
        const savedState = this.readState();
        if (savedState.sameColour && this.sameColorCheckbox) {
          this.sameColorCheckbox.checked = true;
        }
      }

      applySameColourToAll(colour) {
        this._applyingSameColour = true;
        this.selectPrimaryColour(colour);
        const packQuantity = this.selectedPackQuantity;
        for (let i = 2; i <= packQuantity; i++) {
          this.selectPackColour(i, colour);
        }
        this._applyingSameColour = false;
        // Force checkbox to remain checked after sync
        if (this.sameColorCheckbox) this.sameColorCheckbox.checked = true;
        this.saveState({ sameColour: true });
      }

      restoreState() {
        const state = this.readState();
        const defaultPack = this.querySelector('[data-pack-option].is-selected')?.dataset.packOption || '1';
        const pack = state.pack || defaultPack;
        this.setPack(pack, false);

        const colours = state.colours || {};
        this.visibleColourInputs.forEach((input) => {
          const colourIndex = input.dataset.colourIndex;
          const colour = colours[colourIndex] || input.value || this.selectedNativeColour;
          if (colourIndex === '1') return;
          this.selectPackColour(parseInt(colourIndex, 10), colour, false);
        });

        // Restore checkbox state
        if (state.sameColour && this.sameColorCheckbox) {
          this.sameColorCheckbox.checked = true;
        }
      }

      setPack(pack, shouldSave = true) {
        const normalizedPack = String(parseInt(pack, 10) || 1);
        const selectedButton = this.querySelector(`[data-pack-option="${this.escapeAttributeValue(normalizedPack)}"]`);
        const activeButton = selectedButton || this.querySelector('[data-pack-option]');
        const packQuantity = parseInt(activeButton?.dataset.packQuantity || normalizedPack, 10) || 1;
        const packLabel = activeButton?.dataset.packLabel || `${packQuantity} Pack`;

        this.querySelectorAll('[data-pack-option]').forEach((button) => {
          const isSelected = button === activeButton;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.querySelectorAll('[data-colour-group]').forEach((group) => {
          const colourIndex = parseInt(group.dataset.colourGroup, 10);
          group.toggleAttribute('hidden', colourIndex > packQuantity);
        });

        this.querySelectorAll('[data-colour-label]').forEach((label) => {
          const colourIndex = label.dataset.colourLabel;
          label.textContent = `Bra ${colourIndex}:`;
        });

        if (this.sameColorCount) {
          this.sameColorCount.textContent = packQuantity;
        }
        
        const sameColorWrapper = this.querySelector('.pack-update-selector__same-color-wrapper');
        if (sameColorWrapper) {
          sameColorWrapper.style.display = packQuantity === 1 ? 'none' : '';
        }

        this.colourInputs.forEach((input) => {
          const colourIndex = parseInt(input.dataset.colourIndex, 10);
          const isActive = colourIndex <= packQuantity;
          input.disabled = !isActive;
          if (isActive && !input.value) input.value = this.selectedNativeColour;
        });

        if (this.packInput) this.packInput.value = packLabel;
        if (this.packLabel) this.packLabel.textContent = packLabel;

        this.syncQuantity(packQuantity);
        this.ensureVisibleColours();

        if (this.sameColorCheckbox?.checked) {
          const primaryColour = this.querySelector('[data-colour-property][data-colour-index="1"]')?.value || this.selectedNativeColour;
          if (primaryColour) this.applySameColourToAll(primaryColour);
        }

        this.syncColourSummary();

        if (shouldSave) this.saveState({ pack: String(packQuantity) });
      }

      selectPrimaryColour(colour) {
        const nativeControl = this.findNativeColourControl(colour);
        if (!nativeControl) return;

        this.setColourInput(1, colour);
        this.saveColoursState();

        if (nativeControl.tagName === 'OPTION') {
          const select = nativeControl.closest('select');
          select.value = nativeControl.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        if (!nativeControl.checked) {
          nativeControl.click();
        } else {
          nativeControl.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      selectPackColour(colourIndex, colour, shouldSave = true) {
        const selectedButton = this.findColourButton(colourIndex, colour);
        if (!selectedButton) return;

        if (selectedButton.getAttribute('aria-disabled') === 'true') {
          const fallbackButton = this.querySelector(`[data-colour-target="${colourIndex}"]:not([aria-disabled="true"])`);
          if (fallbackButton && fallbackButton.dataset.colourValue !== colour) {
            this.selectPackColour(colourIndex, fallbackButton.dataset.colourValue, shouldSave);
          }
          return;
        }

        this.querySelectorAll(`[data-colour-target="${colourIndex}"]`).forEach((button) => {
          const isSelected = button.dataset.colourValue === colour;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        const selectedValue = this.querySelector(`[data-colour-value="${colourIndex}"]`);
        if (selectedValue) selectedValue.textContent = colour;
        this.setColourInput(colourIndex, colour);
        this.updateColourImage(colourIndex, selectedButton);
        this.syncColourSummary();
        this.syncCheckboxState();
        if (shouldSave) this.saveColoursState();
      }

      syncFromNativeColour() {
        const nativeColour = this.selectedNativeColour;
        if (!nativeColour) return;

        this.querySelectorAll('[data-colour-target="1"]').forEach((button) => {
          const isSelected = button.dataset.colourValue === nativeColour;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        const selectedButton = this.findColourButton(1, nativeColour);
        const selectedValue = this.querySelector('[data-colour-value="1"]');
        if (selectedValue) selectedValue.textContent = nativeColour;
        this.setColourInput(1, nativeColour);
        this.updateColourImage(1, selectedButton);
        this.ensureVisibleColours();
        this.syncColourSummary();
        this.syncCheckboxState();
      }

      ensureVisibleColours() {
        this.visibleColourInputs.forEach((input) => {
          const colourIndex = parseInt(input.dataset.colourIndex, 10);
          const selectedButton = input.value && this.findColourButton(colourIndex, input.value);

          if (selectedButton && selectedButton.getAttribute('aria-disabled') !== 'true') {
            if (colourIndex === 1) {
              this.updateColourImage(colourIndex, selectedButton);
            } else {
              this.selectPackColour(colourIndex, input.value, false);
            }
            return;
          }

          const fallbackButton =
            this.querySelector(`[data-colour-target="${colourIndex}"].is-selected:not([aria-disabled="true"])`) ||
            this.querySelector(`[data-colour-target="${colourIndex}"]:not([aria-disabled="true"])`);

          if (fallbackButton) this.selectPackColour(colourIndex, fallbackButton.dataset.colourValue, false);
        });
      }

      syncQuantity(packQuantity) {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;
        const quantityInput = this.productInfo?.querySelector(`input[name="quantity"][form="product-form-${sectionId}"]`);
        if (!quantityInput) return;

        if (!quantityInput.dataset.packBaseMin) quantityInput.dataset.packBaseMin = quantityInput.getAttribute('min') || '1';

        const baseMin = parseInt(quantityInput.dataset.packBaseMin, 10) || 1;
        const quantity = Math.max(packQuantity, baseMin);

        quantityInput.value = String(quantity);
        quantityInput.dataset.min = String(quantity);
        quantityInput.min = String(quantity);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onPackSubmit(event) {
        const packQuantity = this.selectedPackQuantity;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.isAddingToCart) return;

        const selectedItems = this.selectedColourItems;
        if (selectedItems.length !== packQuantity || selectedItems.some((item) => !item.variantId || !item.colour)) {
          this.productFormElement?.handleErrorMessage?.(window.variantStrings?.unavailable || 'This option is unavailable.');
          return;
        }

        this.addPackToCart(selectedItems);
      }

      addPackToCart(selectedItems) {
        const productForm = this.productFormElement;
        const submitButton = productForm?.querySelector('[type="submit"]');
        const spinner = productForm?.querySelector('.loading__spinner');
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.isAddingToCart = true;
        productForm?.handleErrorMessage?.();
        submitButton?.setAttribute('aria-disabled', true);
        submitButton?.classList.add('loading');
        spinner?.classList.remove('hidden');

        const groupedItems = selectedItems.reduce((groups, item) => {
          const key = `${item.variantId}::${item.colour}`;
          if (!groups[key]) {
            groups[key] = {
              id: Number(item.variantId),
              quantity: 0,
              // properties: {
              //   Pack: this.selectedPackLabel,
              //   Colour: item.colour,
              // },
            };
          }
          groups[key].quantity += 1;
          return groups;
        }, {});

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const body = {
          items: Object.values(groupedItems),
        };

        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await response.json();
            if (!response.ok) throw data;
            return data;
          })
          .then((response) => {
            document.dispatchEvent(new CustomEvent('product:added', { detail: { product: response }, bubbles: true }));

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: selectedItems[0]?.variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              productForm?.handleErrorMessage?.(response.description);
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: selectedItems[0]?.variantId,
              cartData: response,
            });

            if (cart?.renderContents && response.sections) {
              if (cart.tagName === 'CART-NOTIFICATION' && !response.key) {
                response.key = response.items?.[response.items.length - 1]?.key;
              }
              cart.renderContents(response);
            } else {
              cart?.open?.();
            }
          })
          .catch((error) => {
            console.error(error);
            productForm?.handleErrorMessage?.(
              error?.description || error?.message || window.variantStrings?.unavailable || 'Unable to add this selection to the cart.'
            );
          })
          .finally(() => {
            this.isAddingToCart = false;
            submitButton?.classList.remove('loading');
            submitButton?.removeAttribute('aria-disabled');
            spinner?.classList.add('hidden');
            if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      updateColourImage(colourIndex, button) {
        const imageWrapper = this.querySelector(`[data-colour-image="${colourIndex}"]`);
        const image = imageWrapper?.querySelector('img');
        const imageUrl = button?.dataset.variantImage;
        const colour = button?.dataset.colourValue || '';
        if (!image || !imageUrl) return;

        image.src = imageUrl;
        image.srcset = '';
        image.alt = colour;
      }

      syncColourSummary() {
        if (!this.colourSummary) return;
        this.colourSummary.textContent = this.visibleColourInputs.map((input) => input.value).filter(Boolean).join(', ');
      }

      setColourInput(colourIndex, colour) {
        const input = this.querySelector(`[data-colour-property][data-colour-index="${colourIndex}"]`);
        if (input) input.value = colour;
      }

      syncCheckboxState() {
        if (!this.sameColorCheckbox) return;
        if (this._applyingSameColour) return; // Skip while bulk-applying
        const visibleInputs = this.visibleColourInputs;
        if (visibleInputs.length <= 1) return;
        
        const firstColor = visibleInputs[0].value;
        const allSame = firstColor && visibleInputs.every(input => input.value === firstColor);
        
        this.sameColorCheckbox.checked = !!allSame;
        this.saveState({ sameColour: !!allSame });
      }

      findColourButton(colourIndex, colour) {
        if (!colour) return null;
        return this.querySelector(`[data-colour-target="${colourIndex}"][data-colour-value="${this.escapeAttributeValue(colour)}"]`);
      }

      findNativeColourControl(colour) {
        if (!colour) return null;
        const escapedColour = this.escapeAttributeValue(colour);
        return (
          this.variantSelects?.querySelector(`.pack-update-native-color input[value="${escapedColour}"]`) ||
          this.variantSelects?.querySelector(`.pack-update-native-color option[value="${escapedColour}"]`)
        );
      }

      get selectedNativeColour() {
        const checkedInput = this.variantSelects?.querySelector('.pack-update-native-color input:checked');
        if (checkedInput) return checkedInput.value;

        const selectedOption = this.variantSelects?.querySelector('.pack-update-native-color option[selected]');
        if (selectedOption) return selectedOption.value;

        const select = this.variantSelects?.querySelector('.pack-update-native-color select');
        return select?.value || '';
      }

      get selectedPackButton() {
        return this.querySelector('[data-pack-option].is-selected') || this.querySelector('[data-pack-option]');
      }

      get selectedPackQuantity() {
        return parseInt(this.selectedPackButton?.dataset.packQuantity, 10) || 1;
      }

      get selectedPackLabel() {
        return this.selectedPackButton?.dataset.packLabel || `${this.selectedPackQuantity} Pack`;
      }

      get colourInputs() {
        return Array.from(this.querySelectorAll('[data-colour-property]'));
      }

      get visibleColourInputs() {
        return this.colourInputs.filter((input) => {
          const colourIndex = parseInt(input.dataset.colourIndex, 10);
          return colourIndex <= this.selectedPackQuantity;
        });
      }

      get selectedColourItems() {
        return this.visibleColourInputs.map((input) => {
          const colourIndex = parseInt(input.dataset.colourIndex, 10);
          const colour = input.value || this.selectedNativeColour;
          const button = this.findColourButton(colourIndex, colour);
          const variantId = button?.dataset.variantId;

          return {
            colour,
            variantId,
          };
        });
      }

      get storageKey() {
        return `pack-update-selector:${this.dataset.productId}`;
      }

      readState() {
        window.__packUpdateSelectorState = window.__packUpdateSelectorState || {};
        return window.__packUpdateSelectorState[this.storageKey] || {};
      }

      saveState(nextState) {
        window.__packUpdateSelectorState = window.__packUpdateSelectorState || {};
        window.__packUpdateSelectorState[this.storageKey] = { ...this.readState(), ...nextState };
      }

      saveColoursState() {
        const colours = this.colourInputs.reduce((values, input) => {
          values[input.dataset.colourIndex] = input.value;
          return values;
        }, {});
        this.saveState({ colours });
      }

      escapeAttributeValue(value) {
        if (window.CSS?.escape) return CSS.escape(value);
        return String(value).replace(/"/g, '\\"');
      }
    }
  );
}


//When a pack is selected, there should be an option to select only one color variant Code


// if (!customElements.get('pack-update-selector')) {
//   customElements.define(
//     'pack-update-selector',
//     class PackUpdateSelector extends HTMLElement {
//       connectedCallback() {
//         if (this.isConnectedToPackSelector) return;
//         this.isConnectedToPackSelector = true;

//         this.variantSelects = this.closest('variant-selects');
//         this.productInfo = this.closest('product-info');
//         this.packInput = this.querySelector('[data-pack-property]');
//         this.packLabel = this.querySelector('[data-pack-label-value]');
//         this.colourSummary = this.querySelector('[data-colour-summary]');
//         this.productFormElement = this.productInfo?.querySelector('product-form');
//         this.form = this.productFormElement?.querySelector('form');

//         this.restoreState();
//         this.syncFromNativeColour();
//         this.bindEvents();
//       }

//       bindEvents() {
//         this.addEventListener('click', (event) => {
//           const packButton = event.target.closest('[data-pack-option]');
//           if (packButton) {
//             this.setPack(packButton.dataset.packOption);
//             return;
//           }

//           const colourButton = event.target.closest('[data-colour-target]');
//           if (!colourButton || colourButton.getAttribute('aria-disabled') === 'true') return;

//           const colourIndex = parseInt(colourButton.dataset.colourTarget, 10);
//           if (colourIndex === 1) {
//             this.selectPrimaryColour(colourButton.dataset.colourValue);
//           } else {
//             this.selectPackColour(colourIndex, colourButton.dataset.colourValue);
//           }
//         });

//         this.variantSelects?.addEventListener('change', () => this.syncFromNativeColour());

//         if (this.form) {
//           if (this.form._packUpdateSubmitHandler) {
//             this.form.removeEventListener('submit', this.form._packUpdateSubmitHandler, true);
//           }
//           this.form._packUpdateSubmitHandler = this.onPackSubmit.bind(this);
//           this.form.addEventListener('submit', this.form._packUpdateSubmitHandler, true);
//         }
//       }

//       restoreState() {
//         const state = this.readState();
//         const defaultPack = this.querySelector('[data-pack-option].is-selected')?.dataset.packOption || '1';
//         const pack = state.pack || defaultPack;
//         this.setPack(pack, false);

//         const colours = state.colours || {};
//         const colour = colours['1'] || this.selectedPackColour || this.selectedNativeColour;
//         if (colour) this.applyColourToActiveInputs(colour);
//       }

//       setPack(pack, shouldSave = true) {
//         const normalizedPack = String(parseInt(pack, 10) || 1);
//         const selectedButton = this.querySelector(`[data-pack-option="${this.escapeAttributeValue(normalizedPack)}"]`);
//         const activeButton = selectedButton || this.querySelector('[data-pack-option]');
//         const packQuantity = parseInt(activeButton?.dataset.packQuantity || normalizedPack, 10) || 1;
//         const packLabel = activeButton?.dataset.packLabel || `${packQuantity} Pack`;

//         this.querySelectorAll('[data-pack-option]').forEach((button) => {
//           const isSelected = button === activeButton;
//           button.classList.toggle('is-selected', isSelected);
//           button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
//         });

//         this.querySelectorAll('[data-colour-group]').forEach((group) => {
//           const colourIndex = parseInt(group.dataset.colourGroup, 10);
//           group.toggleAttribute('hidden', colourIndex !== 1);
//         });

//         this.querySelectorAll('[data-colour-label]').forEach((label) => {
//           const colourIndex = label.dataset.colourLabel;
//           label.textContent = packQuantity > 1 ? `Colour ${colourIndex}:` : 'Colour:';
//         });

//         this.colourInputs.forEach((input) => {
//           const colourIndex = parseInt(input.dataset.colourIndex, 10);
//           const isActive = colourIndex <= packQuantity;
//           input.disabled = !isActive;
//           if (isActive) input.value = this.selectedPackColour || this.selectedNativeColour;
//         });

//         if (this.packInput) this.packInput.value = packLabel;
//         if (this.packLabel) this.packLabel.textContent = packLabel;

//         this.syncQuantity(packQuantity);
//         this.ensureVisibleColours();
//         this.syncColourSummary();

//         if (shouldSave) this.saveState({ pack: String(packQuantity) });
//       }

//       selectPrimaryColour(colour) {
//         const nativeControl = this.findNativeColourControl(colour);
//         if (!nativeControl) return;

//         this.selectPackColour(1, colour, false);
//         this.saveColoursState();

//         if (nativeControl.tagName === 'OPTION') {
//           const select = nativeControl.closest('select');
//           select.value = nativeControl.value;
//           select.dispatchEvent(new Event('change', { bubbles: true }));
//           return;
//         }

//         if (!nativeControl.checked) {
//           nativeControl.click();
//         } else {
//           nativeControl.dispatchEvent(new Event('change', { bubbles: true }));
//         }
//       }

//       selectPackColour(colourIndex, colour, shouldSave = true) {
//         const selectedButton = this.findColourButton(1, colour);
//         if (!selectedButton) return;

//         if (selectedButton.getAttribute('aria-disabled') === 'true') {
//           const fallbackButton = this.querySelector('[data-colour-target="1"]:not([aria-disabled="true"])');
//           if (fallbackButton && fallbackButton.dataset.colourValue !== colour) {
//             this.selectPackColour(1, fallbackButton.dataset.colourValue, shouldSave);
//           }
//           return;
//         }

//         this.querySelectorAll('[data-colour-target="1"]').forEach((button) => {
//           const isSelected = button.dataset.colourValue === colour;
//           button.classList.toggle('is-selected', isSelected);
//           button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
//         });

//         const selectedValue = this.querySelector('[data-colour-value="1"]');
//         if (selectedValue) selectedValue.textContent = colour;
//         this.applyColourToActiveInputs(colour);
//         this.updateColourImage(1, selectedButton);
//         this.syncColourSummary();
//         if (shouldSave) this.saveColoursState();
//       }

//       syncFromNativeColour() {
//         const nativeColour = this.selectedNativeColour;
//         if (!nativeColour) return;

//         this.querySelectorAll('[data-colour-target="1"]').forEach((button) => {
//           const isSelected = button.dataset.colourValue === nativeColour;
//           button.classList.toggle('is-selected', isSelected);
//           button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
//         });

//         const selectedButton = this.findColourButton(1, nativeColour);
//         const selectedValue = this.querySelector('[data-colour-value="1"]');
//         if (selectedValue) selectedValue.textContent = nativeColour;
//         this.applyColourToActiveInputs(nativeColour);
//         this.updateColourImage(1, selectedButton);
//         this.ensureVisibleColours();
//         this.syncColourSummary();
//       }

//       ensureVisibleColours() {
//         const colour = this.selectedPackColour || this.selectedNativeColour;
//         const selectedButton = colour && this.findColourButton(1, colour);

//         if (selectedButton && selectedButton.getAttribute('aria-disabled') !== 'true') {
//           this.selectPackColour(1, colour, false);
//           return;
//         }

//         const fallbackButton =
//           this.querySelector('[data-colour-target="1"].is-selected:not([aria-disabled="true"])') ||
//           this.querySelector('[data-colour-target="1"]:not([aria-disabled="true"])');

//         if (fallbackButton) this.selectPackColour(1, fallbackButton.dataset.colourValue, false);
//       }

//       syncQuantity(packQuantity) {
//         const quantityInput = this.productInfo?.querySelector(`input[name="quantity"][form="product-form-${this.dataset.sectionId}"]`);
//         if (!quantityInput) return;

//         if (!quantityInput.dataset.packBaseMin) quantityInput.dataset.packBaseMin = quantityInput.getAttribute('min') || '1';

//         const baseMin = parseInt(quantityInput.dataset.packBaseMin, 10) || 1;
//         const quantity = Math.max(packQuantity, baseMin);

//         quantityInput.value = String(quantity);
//         quantityInput.dataset.min = String(quantity);
//         quantityInput.min = String(quantity);
//         quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
//       }

//       onPackSubmit(event) {
//         const packQuantity = this.selectedPackQuantity;

//         event.preventDefault();
//         event.stopImmediatePropagation();

//         if (this.isAddingToCart) return;

//         const selectedItems = this.selectedColourItems;
//         if (selectedItems.length !== packQuantity || selectedItems.some((item) => !item.variantId || !item.colour)) {
//           this.productFormElement?.handleErrorMessage?.(window.variantStrings?.unavailable || 'This option is unavailable.');
//           return;
//         }

//         this.addPackToCart(selectedItems);
//       }

//       addPackToCart(selectedItems) {
//         const productForm = this.productFormElement;
//         const submitButton = productForm?.querySelector('[type="submit"]');
//         const spinner = productForm?.querySelector('.loading__spinner');
//         const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

//         this.isAddingToCart = true;
//         productForm?.handleErrorMessage?.();
//         submitButton?.setAttribute('aria-disabled', true);
//         submitButton?.classList.add('loading');
//         spinner?.classList.remove('hidden');

//         const groupedItems = selectedItems.reduce((groups, item) => {
//           const key = `${item.variantId}::${item.colour}`;
//           if (!groups[key]) {
//             groups[key] = {
//               id: Number(item.variantId),
//               quantity: 0,
//               // properties: {
//               //   Pack: this.selectedPackLabel,
//               //   Colour: item.colour,
//               // },
//             };
//           }
//           groups[key].quantity += 1;
//           return groups;
//         }, {});

//         const config = fetchConfig('javascript');
//         config.headers['X-Requested-With'] = 'XMLHttpRequest';

//         const body = {
//           items: Object.values(groupedItems),
//         };

//         if (cart?.getSectionsToRender) {
//           body.sections = cart.getSectionsToRender().map((section) => section.id);
//           body.sections_url = window.location.pathname;
//           cart.setActiveElement?.(document.activeElement);
//         }

//         config.body = JSON.stringify(body);

//         fetch(`${routes.cart_add_url}`, config)
//           .then(async (response) => {
//             const data = await response.json();
//             if (!response.ok) throw data;
//             return data;
//           })
//           .then((response) => {
//             document.dispatchEvent(new CustomEvent('product:added', { detail: { product: response }, bubbles: true }));

//             if (response.status) {
//               publish(PUB_SUB_EVENTS.cartError, {
//                 source: 'product-form',
//                 productVariantId: selectedItems[0]?.variantId,
//                 errors: response.errors || response.description,
//                 message: response.message,
//               });
//               productForm?.handleErrorMessage?.(response.description);
//               return;
//             }

//             publish(PUB_SUB_EVENTS.cartUpdate, {
//               source: 'product-form',
//               productVariantId: selectedItems[0]?.variantId,
//               cartData: response,
//             });

//             if (cart?.renderContents && response.sections) {
//               if (cart.tagName === 'CART-NOTIFICATION' && !response.key) {
//                 response.key = response.items?.[response.items.length - 1]?.key;
//               }
//               cart.renderContents(response);
//             } else {
//               cart?.open?.();
//             }
//           })
//           .catch((error) => {
//             console.error(error);
//             productForm?.handleErrorMessage?.(
//               error?.description || error?.message || window.variantStrings?.unavailable || 'Unable to add this selection to the cart.'
//             );
//           })
//           .finally(() => {
//             this.isAddingToCart = false;
//             submitButton?.classList.remove('loading');
//             submitButton?.removeAttribute('aria-disabled');
//             spinner?.classList.add('hidden');
//             if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
//           });
//       }

//       updateColourImage(colourIndex, button) {
//         const imageWrapper = this.querySelector(`[data-colour-image="${colourIndex}"]`);
//         const image = imageWrapper?.querySelector('img');
//         const imageUrl = button?.dataset.variantImage;
//         const colour = button?.dataset.colourValue || '';
//         if (!image || !imageUrl) return;

//         image.src = imageUrl;
//         image.srcset = '';
//         image.alt = colour;
//       }

//       syncColourSummary() {
//         if (!this.colourSummary) return;
//         this.colourSummary.textContent = this.selectedPackColour || this.selectedNativeColour;
//       }

//       setColourInput(colourIndex, colour) {
//         const input = this.querySelector(`[data-colour-property][data-colour-index="${colourIndex}"]`);
//         if (input) input.value = colour;
//       }

//       applyColourToActiveInputs(colour) {
//         this.visibleColourInputs.forEach((input) => {
//           input.value = colour;
//         });
//       }

//       findColourButton(colourIndex, colour) {
//         return this.querySelector(`[data-colour-target="${colourIndex}"][data-colour-value="${this.escapeAttributeValue(colour)}"]`);
//       }

//       findNativeColourControl(colour) {
//         const escapedColour = this.escapeAttributeValue(colour);
//         return (
//           this.variantSelects?.querySelector(`.pack-update-native-color input[value="${escapedColour}"]`) ||
//           this.variantSelects?.querySelector(`.pack-update-native-color option[value="${escapedColour}"]`)
//         );
//       }

//       get selectedNativeColour() {
//         const checkedInput = this.variantSelects?.querySelector('.pack-update-native-color input:checked');
//         if (checkedInput) return checkedInput.value;

//         const selectedOption = this.variantSelects?.querySelector('.pack-update-native-color option[selected]');
//         if (selectedOption) return selectedOption.value;

//         const select = this.variantSelects?.querySelector('.pack-update-native-color select');
//         return select?.value || '';
//       }

//       get selectedPackButton() {
//         return this.querySelector('[data-pack-option].is-selected') || this.querySelector('[data-pack-option]');
//       }

//       get selectedPackQuantity() {
//         return parseInt(this.selectedPackButton?.dataset.packQuantity, 10) || 1;
//       }

//       get selectedPackLabel() {
//         return this.selectedPackButton?.dataset.packLabel || `${this.selectedPackQuantity} Pack`;
//       }

//       get colourInputs() {
//         return Array.from(this.querySelectorAll('[data-colour-property]'));
//       }

//       get visibleColourInputs() {
//         return this.colourInputs.filter((input) => {
//           const colourIndex = parseInt(input.dataset.colourIndex, 10);
//           return colourIndex <= this.selectedPackQuantity;
//         });
//       }

//       get selectedPackColour() {
//         return this.querySelector('[data-colour-property][data-colour-index="1"]')?.value || '';
//       }

//       get selectedColourItems() {
//         return this.visibleColourInputs.map((input) => {
//           const colour = input.value || this.selectedNativeColour;
//           const button = this.findColourButton(1, colour);
//           const variantId = button?.dataset.variantId;

//           return {
//             colour,
//             variantId,
//           };
//         });
//       }

//       get storageKey() {
//         return `pack-update-selector:${this.dataset.productId}`;
//       }

//       readState() {
//         window.__packUpdateSelectorState = window.__packUpdateSelectorState || {};
//         return window.__packUpdateSelectorState[this.storageKey] || {};
//       }

//       saveState(nextState) {
//         window.__packUpdateSelectorState = window.__packUpdateSelectorState || {};
//         window.__packUpdateSelectorState[this.storageKey] = { ...this.readState(), ...nextState };
//       }

//       saveColoursState() {
//         const colours = this.colourInputs.reduce((values, input) => {
//           values[input.dataset.colourIndex] = input.value;
//           return values;
//         }, {});
//         this.saveState({ colours });
//       }

//       escapeAttributeValue(value) {
//         if (window.CSS?.escape) return CSS.escape(value);
//         return String(value).replace(/"/g, '\\"');
//       }
//     }
//   );
// }
